import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

/**
 * Shared empty-state component for every admin list. Use it any time
 * a panel would otherwise show an awkward "no data" state.
 *
 * Props:
 *   - `icon`       : lucide-react icon for the empty illustration
 *   - `title`      : short headline (e.g. "No bookings yet")
 *   - `description`: 1–2 sentences explaining what's missing + what to do
 *   - `action`     : optional CTA element (a button or link)
 *   - `tone`       : 'default' | 'muted' — visual intensity
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'default',
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  tone?: 'default' | 'muted';
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={clsx(
        'flex flex-col items-center justify-center px-6 py-14 text-center',
        tone === 'muted' ? 'opacity-90' : '',
      )}
    >
      <div
        className={clsx(
          'mb-4 flex h-16 w-16 items-center justify-center rounded-2xl',
          tone === 'muted'
            ? 'bg-white/[0.03] text-white/40'
            : 'bg-[#FFD60A]/10 text-[#FFD60A]',
        )}
      >
        {icon}
      </div>
      <h3 className="text-base font-bold text-[#FAFAFA]">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-[#8E8E93]">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </motion.div>
  );
}